import {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import clsx from 'clsx'
import 'react-toastify/dist/ReactToastify.css'
import notifyToast from '../../components/Toast/Toast'
import TitleComponent from '../../components/TitleComponent/TitleComponent'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import {ThermoBoardNalozi} from '../../models/ThermoBoardNalozi'
import {fetchTBNalog, saveTBNalog, updateTBNalog} from '../../api/thermoboardNalozi'
import PanelTipDropdown from '../paneli/components/PanelTipDropdown/PanelTipDropdown'
import {useNavigate} from 'react-router-dom'

type ThermoBoardFormValues = Pick<Partial<ThermoBoardNalozi>, 'TipPanela' | 'Napomena'>

const initialValues: ThermoBoardFormValues = {
    TipPanela: '',
    Napomena: '',
}

const validationSchema = Yup.object({
    TipPanela: Yup.string().required('Obavezno polje'),
    Napomena: Yup.string().nullable(),
})

const ThermoBoardDetails: React.FC = () => {
    const {id} = useParams()
    const idFromUrl = Number(id)
    const isEditMode = Boolean(idFromUrl)

    const [loading, setLoading] = useState(true)
    const [formValues, setFormValues] = useState<ThermoBoardFormValues>(initialValues)
    const navigate = useNavigate()

    useEffect(() => {
        let mounted = true

        const loadNalog = async () => {
            if (!isEditMode) {
                setLoading(false)
                return
            }

            try {
                const data = await fetchTBNalog(idFromUrl)

                if (mounted) {
                    setFormValues({
                        TipPanela: data?.TipPanela ?? '',
                        Napomena: data?.Napomena ?? '',
                    })
                }
            } catch (error: any) {
                notifyToast(false, error?.response?.data?.message || 'Nalog nije pronadjen.')
            } finally {
                if (mounted) {
                    setLoading(false)
                }
            }
        }

        loadNalog()

        return () => {
            mounted = false
        }
    }, [idFromUrl, isEditMode])

    const formik = useFormik<ThermoBoardFormValues>({
        initialValues: formValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values, {setSubmitting}) => {
            try {
                const payload = {
                    TipPanela: values.TipPanela,
                    Napomena: values.Napomena,
                }

                const response = isEditMode
                    ? await updateTBNalog(payload, idFromUrl)
                    : await saveTBNalog(payload)

                notifyToast(response.error === 0, response.message || 'Podaci su snimljeni.')
                setTimeout(() => {
                    navigate(-1)
                }, 1500) // small delay so user sees toast
            } catch (error: any) {
                notifyToast(
                    false,
                    error?.response?.data?.message || error?.message || 'Greska pri snimanju.'
                )
            } finally {
                setSubmitting(false)
            }
        },
    })

    const hasError = (field: keyof ThermoBoardFormValues) =>
        Boolean(formik.touched[field] && formik.errors[field])

    const fieldClass = (field: keyof ThermoBoardFormValues) =>
        clsx('form-control form-control-lg form-control-solid', {
            'is-invalid': hasError(field),
            'is-valid': formik.touched[field] && !formik.errors[field],
        })

    const renderError = (field: keyof ThermoBoardFormValues) => {
        if (!hasError(field)) {
            return null
        }

        return (
            <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>{formik.errors[field]}</div>
            </div>
        )
    }

    return (
        <div className='card shadow-sm mb-5 mb-xl-10'>
            <ToastContainer/>

            <div className='card-header border-0'>
                <div className='card-title m-0'>
                    <TitleComponent name={isEditMode ? 'Thermo Board naloga' : 'Novi ThermoBoard nalog'}/>
                </div>
            </div>

            {loading ? (
                <LoadingComponent/>
            ) : (
                <form onSubmit={formik.handleSubmit} noValidate className='form'>
                    <div className='card-body border-top p-9'>
                        <div className='mb-10'>
                            <div className='fs-4 fw-bold text-gray-900 mb-5'>Podaci o nalogu</div>
                            <div className='separator separator-dashed mb-6'></div>

                            <div className='row g-5'>
                                <div className='col-lg-5 col-md-7'>
                                    <label className='form-label required fw-bold fs-6'>Tip panela</label>
                                    <PanelTipDropdown
                                        value={formik.values.TipPanela}
                                        onChange={(_, value) => formik.setFieldValue('TipPanela', value)}
                                        errors={formik.errors.TipPanela}
                                        touched={formik.touched.TipPanela}
                                        selectName='TipPanela'
                                    />
                                </div>
                                <div className='col-5 mt-10'>
                                    <div className='alert alert-info'>
                                        Nije moguce kreirati vise od jednog naloga za isti tip panela.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className='fs-4 fw-bold text-gray-900 mb-5'>Napomena</div>
                            <div className='separator separator-dashed mb-6'></div>

                            <textarea
                                rows={4}
                                placeholder='Napomena'
                                {...formik.getFieldProps('Napomena')}
                                className={fieldClass('Napomena')}
                            />
                            {renderError('Napomena')}
                        </div>
                    </div>

                    <div className='card-footer d-flex justify-content-end py-6 px-9'>
                        <button
                            type='submit'
                            className='btn btn-primary px-8'
                            disabled={formik.isSubmitting || !formik.isValid}
                        >
                            {!formik.isSubmitting && 'Potvrdi'}
                            {formik.isSubmitting && (
                                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
                            )}
                        </button>
                    </div>
                </form>
            )}
        </div>
    )
}

export default ThermoBoardDetails
